import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  Stack,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import TableChartIcon from '@mui/icons-material/TableChart';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import api from '../api/axios';
import { outcomeModuleDisplayName } from '../utils/outcomeModuleName';
import AssessmentPayloadEditor, { KIND_LABELS } from './AssessmentPayloadEditor';
import {
  MODULE_EXAM_TASK_CODE,
  filterTaskScopedAssessments,
  filterModuleExamAssessments,
  findDuplicateQuestionPairs
} from '../utils/taskAssessmentIacAudit';

function qCount(p) {
  return Array.isArray(p?.questions) ? p.questions.length : 0;
}

function marksTotal(p) {
  return Array.isArray(p?.questions)
    ? p.questions.reduce((s, q) => s + (Number(q.marks) || 0), 0)
    : 0;
}

export default function ModuleAssessmentsListPanel({
  qualificationId,
  module,
  parentAssessmentListVersion,
  onAssessmentsChanged
}) {
  const [assessments, setAssessments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [editPayload, setEditPayload] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [exportLoading, setExportLoading] = useState(false);

  const load = useCallback(async () => {
    if (!qualificationId || !module?.id) return;
    setLoading(true);
    try {
      const res = await api.get(
        `/qualifications/${qualificationId}/learning-content/task-assessments`,
        { params: { module_id: module.id } }
      );
      setAssessments(res.data?.assessments || []);
    } catch (_) {
      setAssessments([]);
    } finally {
      setLoading(false);
    }
  }, [qualificationId, module?.id]);

  useEffect(() => {
    load();
  }, [load, parentAssessmentListVersion]);

  const taskScoped = useMemo(
    () => filterTaskScopedAssessments(assessments),
    [assessments]
  );
  const moduleExams = useMemo(
    () => filterModuleExamAssessments(assessments),
    [assessments]
  );

  const openRow = (r) => {
    setSelected(r);
    setEditPayload(r.payload ? JSON.parse(JSON.stringify(r.payload)) : { questions: [] });
  };

  const closeEditor = () => {
    setSelected(null);
    setEditPayload(null);
  };

  const saveEdit = async (nextStatus) => {
    if (!selected) return;
    if (nextStatus === 'approved' && editPayload) {
      const dups = findDuplicateQuestionPairs(editPayload.questions || []);
      if (dups.length > 0) {
        const ok = window.confirm(
          `${dups.length} possible duplicate question pair(s) detected. Approve anyway?`
        );
        if (!ok) return;
      }
    }
    setSaving(true);
    try {
      await api.put(
        `/qualifications/${qualificationId}/learning-content/task-assessments/${selected.id}`,
        { payload: editPayload, status: nextStatus }
      );
      if (onAssessmentsChanged) onAssessmentsChanged();
      await load();
      closeEditor();
    } catch (e) {
      alert(e.response?.data?.message || e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const performDelete = async (id) => {
    try {
      await api.delete(
        `/qualifications/${qualificationId}/learning-content/task-assessments/${id}`
      );
      if (onAssessmentsChanged) onAssessmentsChanged();
      await load();
      if (selected?.id === id) closeEditor();
    } catch (e) {
      alert(e.response?.data?.message || e.message || 'Delete failed');
    } finally {
      setConfirmDeleteId(null);
    }
  };

  const moduleTitle = outcomeModuleDisplayName(module);

  const downloadExport = async (format, assessmentId = null) => {
    if (!qualificationId || !module?.id) return;
    setExportLoading(true);
    try {
      const params = { module_id: module.id, format };
      if (assessmentId != null) params.assessment_id = assessmentId;
      const res = await api.get(
        `/qualifications/${qualificationId}/learning-content/task-assessments/export`,
        {
          params,
          responseType: 'blob'
        }
      );
      const mimeMap = {
        pdf: 'application/pdf',
        xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        csv: 'text/csv',
        zip: 'application/zip'
      };
      const extMap = { pdf: 'pdf', xlsx: 'xlsx', csv: 'csv', zip: 'zip' };
      const mime = mimeMap[format] || 'application/octet-stream';
      const ext = extMap[format] || format;
      const blob = new Blob([res.data], { type: mime });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = assessmentId
        ? `assessment-${assessmentId}.${ext}`
        : `assessments-m${module.id}.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      const data = e.response?.data;
      if (data instanceof Blob) {
        try {
          const text = await data.text();
          const j = JSON.parse(text);
          alert(j.message || 'Export failed');
        } catch {
          alert('Export failed');
        }
      } else {
        alert(e.response?.data?.message || e.message || 'Export failed');
      }
    } finally {
      setExportLoading(false);
    }
  };

  return (
    <Box>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        useFlexGap
        spacing={1}
        sx={{ mb: 1 }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Generated assessments — {moduleTitle}
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Button
            size="small"
            variant="contained"
            disabled={loading || !module?.id || exportLoading || assessments.length === 0}
            onClick={() => downloadExport('zip')}
          >
            Export all (ZIP)
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={<TableChartIcon />}
            disabled={loading || !module?.id || exportLoading || assessments.length === 0}
            onClick={() => downloadExport('xlsx')}
          >
            Export Excel
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={<PictureAsPdfIcon />}
            disabled={loading || !module?.id || exportLoading || assessments.length === 0}
            onClick={() => downloadExport('pdf')}
          >
            Export PDF
          </Button>
        </Stack>
      </Stack>
      {loading ? (
        <Typography color="text.secondary">Loading…</Typography>
      ) : assessments.length === 0 ? (
        <Typography color="text.secondary" sx={{ mb: 2 }}>
          No assessments yet. Create a task assessment or a module exam.
        </Typography>
      ) : (
        <>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
            Task-scoped assessments
          </Typography>
          {taskScoped.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              None yet.
            </Typography>
          ) : (
            <Table size="small" sx={{ mb: 3 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Task</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Questions</TableCell>
                  <TableCell>Marks</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {taskScoped.map((r) => (
                  <TableRow key={r.id} hover>
                    <TableCell>{r.task_code}</TableCell>
                    <TableCell>{KIND_LABELS[r.kind] || r.kind}</TableCell>
                    <TableCell>{qCount(r.payload)}</TableCell>
                    <TableCell>{marksTotal(r.payload)}</TableCell>
                    <TableCell>
                      <Chip size="small" label={r.status} />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Export CSV">
                        <IconButton
                          size="small"
                          disabled={r.kind !== 'mcq' || exportLoading}
                          onClick={() => downloadExport('csv', r.id)}
                        >
                          <TableChartIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit">
                        <IconButton size="small" color="primary" onClick={() => openRow(r)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => setConfirmDeleteId(r.id)}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
            Module exams
          </Typography>
          {moduleExams.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              None yet.
            </Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Label</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Questions</TableCell>
                  <TableCell>Marks</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {moduleExams.map((r) => (
                  <TableRow key={r.id} hover>
                    <TableCell>
                      {r.task_code === MODULE_EXAM_TASK_CODE ? 'Module exam' : r.task_code}
                    </TableCell>
                    <TableCell>{KIND_LABELS[r.kind] || r.kind}</TableCell>
                    <TableCell>{qCount(r.payload)}</TableCell>
                    <TableCell>{marksTotal(r.payload)}</TableCell>
                    <TableCell>
                      <Chip size="small" label={r.status} />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Export CSV">
                        <IconButton
                          size="small"
                          disabled={r.kind !== 'mcq' || exportLoading}
                          onClick={() => downloadExport('csv', r.id)}
                        >
                          <TableChartIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit">
                        <IconButton size="small" color="primary" onClick={() => openRow(r)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => setConfirmDeleteId(r.id)}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </>
      )}

      <Dialog open={Boolean(selected)} onClose={closeEditor} maxWidth="lg" fullWidth>
        <DialogTitle>
          Edit assessment — {KIND_LABELS[selected?.kind] || selected?.kind} ({selected?.task_code})
        </DialogTitle>
        <DialogContent dividers>
          {selected && editPayload && (
            <>
              <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
                <Chip size="small" label={`${qCount(editPayload)} questions`} />
                <Chip size="small" label={`${marksTotal(editPayload)} marks`} />
                <Chip size="small" label={`Status: ${selected.status}`} />
              </Stack>
              <AssessmentPayloadEditor
                kind={selected.kind}
                payload={editPayload}
                onChange={setEditPayload}
                qualificationId={qualificationId}
                assessmentId={selected.id}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEditor}>Cancel</Button>
          <Button
            color="error"
            variant="outlined"
            startIcon={<DeleteOutlineIcon />}
            onClick={() => selected && setConfirmDeleteId(selected.id)}
          >
            Delete
          </Button>
          <Button variant="outlined" disabled={saving} onClick={() => saveEdit('saved')}>
            Save
          </Button>
          <Button variant="contained" disabled={saving} onClick={() => saveEdit('approved')}>
            Save and approve
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={confirmDeleteId != null}
        onClose={() => setConfirmDeleteId(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete assessment?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">This cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteId(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={() => performDelete(confirmDeleteId)}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
